import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Input,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { ICreateClientInputModel } from 'inputModels';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toPattern } from 'vanilla-masker';
import { ICompanyClientViewModel } from 'viewModels';
import { cnpj as DocumentValidator } from 'cpf-cnpj-validator';
import { NavHeader } from '../../components';
import { Container, Content } from './style';
import { generateRandomPassword } from '../../utils/generateRandomPassword';
import { CompanyController } from '../../controller/CompanyController';
import { useCompany } from '../../context/company';
import { sendNewPasswordEmail } from '../../controller/EmailController';
import { MASK_CNPJ } from '../../constants';
import { useApp } from '../../context';
import { encryptPassword } from '../../utils/encryptPassword';
import { CardClient } from '../../components/Cards/CardClient';
import { unMask } from '../../utils/removeMask';
import { getByCnpj } from '../../services/api/consultCnpj';
import { getAddressByCep } from '../../services/api/cep';
import { slugify } from '../../utils/slugify';
import { getCodeByName } from '../../services/api/ibge';
import { useConfig } from '../../context/configCustom';

export const RegisterClient = () => {
  const { company, setCompany } = useCompany();
  const { pathResourceEmail } = useConfig();
  const { setLoading } = useApp();
  const [messageApi, setMessageApi] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<ICompanyClientViewModel[]>([]);
  const schema = yup.object().shape({
    cnpj: yup
      .string()
      .required('O documento é obrigatório')
      .test('isRequired', 'Digite um cnpj válido', (value: string) =>
        DocumentValidator.isValid(value),
      ),
    email: yup.string().required('O campo e-mail é obrigatório').email('Digite um e-mail válido'),
    name: yup.string().required('O campo nome é obrigatório'),
    password: yup.string().required('O campo senha é obrigatório'),
  });

  useEffect(() => {
    setSelectedCompany(
      company.filter((c) => c.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())),
    );
  }, [company, search]);

  useEffect(() => {
    setSelectedCompany(company);
  }, [company]);

  const [defaultValues] = useState<ICreateClientInputModel>({
    name: '',
    cnpj: '',
    email: '',
    password: '',
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { cnpj } = watch();

  useEffect(() => {
    if (DocumentValidator.isValid(cnpj)) {
      getByCnpj(unMask(cnpj)).then((response) => {
        setValue('name', response.companyName);
      });
    }
  }, [cnpj, setValue]);

  useEffect(() => {
    setValue('password', generateRandomPassword());
  }, [setValue]);

  const disabled = (id: string) => {
    CompanyController.deleteClients(id)
      .then(() => {
        setCompany(
          company.map((c) => {
            const newCompany = c;
            if (newCompany.id === id) newCompany.disabled = !c.disabled;
            return newCompany;
          }),
        );
      })
      .catch(
        ({
          response: {
            data: { Message },
          },
        }) => {
          setMessageApi(Message);
        },
      );
  };

  const onSubmit = async (input: ICreateClientInputModel) => {
    if (!pathResourceEmail) {
      return;
    }
    const consultCnpj = await getByCnpj(unMask(input.cnpj));
    const coordinates = await getAddressByCep(consultCnpj.postalCode);
    const cityFormated = slugify(consultCnpj.city);
    const ibgeData = await getCodeByName(cityFormated, slugify(consultCnpj.uf));

    setLoading(true);
    CompanyController.createClient({
      password: encryptPassword(input.password),
      cnpj: input.cnpj,
      email: input.email,
      name: input.name,
      tradingName: consultCnpj.fantasyName,
      address: {
        uf: consultCnpj.uf,
        ufCode: ibgeData?.ufCode ?? 0,
        city: consultCnpj.city,
        cityCode: Number(ibgeData?.id) ?? 0,
        street: coordinates?.street.slice(0, 99) ?? consultCnpj.street.slice(0, 99),
        number: isNaN(Number(consultCnpj.number)) ? 0 : Number(consultCnpj.number),
        postalCode: consultCnpj.postalCode,
        longitude: coordinates?.location?.coordinates?.longitude ?? 0,
        latitude: coordinates?.location?.coordinates?.latitude ?? 0,
        complement: '',
      },
    })
      .then((response) => {
        setCompany([response, ...company]);
        sendNewPasswordEmail({
          name: input.name,
          password: input.password,
          subject: 'Bem vindo ao legal Hub',
          to: input.email,
          pathResource: pathResourceEmail,
        });
      })
      .catch(
        ({
          response: {
            data: { Message },
          },
        }) => {
          setMessageApi(Message);
        },
      )
      .finally(() => setLoading(false));
  };

  return (
    <>
      <NavHeader typeHeader="company" />
      <Container>
        <Content>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" component="p" color="primary">
                  Cadastrar
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2} md={2} lg={3}>
                <Controller
                  control={control}
                  name="cnpj"
                  render={({ field: { value, ...field } }) => (
                    <TextField
                      size="small"
                      variant="standard"
                      helperText={errors.cnpj?.message}
                      error={!!errors.cnpj?.message}
                      value={MASK_CNPJ ? toPattern(value ?? '', MASK_CNPJ) : MASK_CNPJ}
                      {...field}
                      placeholder="Cnpj"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={5} md={4}>
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <TextField
                      size="small"
                      variant="standard"
                      helperText={errors.name?.message}
                      error={!!errors.name?.message}
                      {...field}
                      placeholder="Nome"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={5} md={4}>
                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <TextField
                      size="small"
                      variant="standard"
                      helperText={errors.email?.message}
                      error={!!errors.email?.message}
                      {...field}
                      placeholder="Email"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2} lg={1}>
                <div className="center">
                  <Button type="submit" variant="contained" fullWidth>
                    <Add style={{ fontSize: '18px' }} /> Criar
                  </Button>
                </div>
              </Grid>
            </Grid>
          </form>
        </Content>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h4" component="p" color="primary">
              Clientes Cadastrados
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Input
              startAdornment={<Search style={{ padding: '0 0.5rem' }} color="primary" />}
              placeholder="Search"
              onChange={({ target: { value } }) => setSearch(value)}
            />
          </Grid>
          {selectedCompany.map((c) => (
            <CardClient key={c.id} c={c} disabled={disabled} />
          ))}
        </Grid>
      </Container>
      <Dialog
        open={!!messageApi}
        onClose={() => setMessageApi(null)}
        aria-labelledby="responsive-dialog-title">
        <DialogTitle id="responsive-dialog-title">Não foi possível Realizar a ação</DialogTitle>
        <DialogContent>
          <DialogContentText>{messageApi}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageApi(null)} autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
